/**
 * Card-specific custom logic registry. Each module exports hooks that the
 * engine calls at specific moments (onAttackPlay, onDefendPlay, onDamageDealt,
 * onDamageTaken, onFullBlock, onRoundStart, onUtilityPlay,etc).
 *
 * The engine first runs data-driven mechanics (mechanics.ts), then consults
 * this registry by canonical card name for any hard-coded special behaviour.
 */
import type { Card } from "@/types/card";
import type { PlayerState, GameState, PendingPrompt, Buff } from "@/types/game";
import { canonicalName } from "../nameMatcher";

import { assistingSaber } from "./assistingSaber";
import { thisIsSparta } from "./thisIsSparta";
import { soulreaver } from "./soulreaver";
import { aegisTheDefiant } from "./aegisTheDefiant";
import { spartacusUndefeated } from "./spartacusUndefeated";
import { hardenedSteel } from "./hardenedSteel";
import { handleBash } from "./handleBash";
import { theFirstFight } from "./theFirstFight";
import { igniteBlade } from "./igniteBlade";
import { clobberation } from "./clobberation";
import { danceOfBlades } from "./danceOfBlades";
import { hardenedHands } from "./hardenedHands";
import { unbrokenSands } from "./unbrokenSands";
import { zeusWrathbrand } from "./zeusWrathbrand";
import { addedPressure } from "./addedPressure";
import { concussionRing } from "./concussionRing";
import { thrustingRelease } from "./thrustingRelease";
import { fleetingCut } from "./fleetingCut";
import { crixusGaulBreaker } from "./crixusGaulBreaker";
import { sawItComing } from "./sawItComing";
import { pocketSand } from "./pocketSand";
import { hookfangShiv } from "./hookfangShiv";
import { bloodforgedPlate } from "./bloodforgedPlate";
import { bloodiedEdge } from "./bloodiedEdge";
import { relentlessEnd } from "./relentlessEnd";
import { reversal } from "./Reversal";
import { blindSideFollowUp } from "./blindSideFollowUp";
import { followUp } from "./followUp";
import { shoulderBash } from "./shoulderBash";
import { sharpeningMill } from "./sharpeningMill";
import { fluidMotion } from "./fluidMotion";
import { readyArms } from "./readyArms";
import { pickUpYourWeapon } from "./pickUpYourWeapon";
import { ironBreakSigil } from "./ironBreakSigil";
import { noTime } from "./noTime";
import { serpentsPrick } from "./serpentsPrick";
import { theFinalStraw } from "./theFinalStraw";
import { wraithSpin } from "./wraithSpin";
import { crimsonTempest } from "./crimsonTempest";
// Phase 3 cards
import { chainTheFlow } from "./chainTheFlow";
import { vengeanceStrike } from "./vengeanceStrike";
import { calculateRisk } from "./calculateRisk";
import { doubledBlock } from "./doubledBlock";

export interface CardHookCtx {
  self: PlayerState;
  opponent: PlayerState;
  game: GameState;
  card: Card;
  defendCard?: Card;
  attackCard?: Card;
  damageDealt?: number;
  damageTaken?: number;
  fullBlock?: boolean;
  /** ZoneCard.id of the card invoking this hook — i.e. the played AD-zone
   *  copy whose hook is firing. Set by combat hooks (onDamageDealt /
   *  onFullBlock / onDamageTaken) AND by onAttackPlay / onDefendPlay so
   *  card logic can return `returnSelfToHand` referencing this exact
   *  AD-zone instance, or surface a prompt that needs the played instance. */
  instanceId?: string;
  /** CombatSlot.id of the active slot when the hook fires. Set by
   *  onAttackPlay (the slot just pushed), onDefendPlay (the slot the defend
   *  attaches to), onDamageDealt / onDamageTaken / onFullBlock. Used by
   *  prompts like `pick-discard-for-bonus` that need to know which slot to
   *  apply the bonus to. */
  slotId?: string;
  /** Optional card lookup map. Set by the engine for hooks that may need to
   *  inspect other cards (e.g. Chain the Flow filters its deck candidates by
   *  keyword; tutors filter by type/weight/etc.). Hooks that don't need it
   *  can ignore it. Map is keyed by Card.id (supabase uuid). */
  cardMap?: Map<string, Card>;
}

export interface CardHookResult {
  selfPatch?: Partial<PlayerState>;
  opponentPatch?: Partial<PlayerState>;
  log?: string[];
  prompt?: PendingPrompt;
  /** Focus tokens to mint to self (preferred over selfPatch.focus). */
  focusGrant?: number;
  /** Focus tokens to mint to opponent. */
  opponentFocusGrant?: number;
  /** Cards self should draw. */
  drawSelf?: number;
  /** Cards opponent should draw. */
  drawOpponent?: number;
  /** Disarm an opponent equipment slot (any usable). */
  disarmOpponentEquipment?: { count?: number };
  /** Keyword grants to add directly to THIS combat slot (e.g. Hardened Hands
   * granting Piercing to its own attack on play). Only honored by the engine
   * when returned from onAttackPlay, onDefendPlay, onUtilityPlay. */
  slotKeywordGrants?: string[];
  /**
   * Add N power to the CURRENT attack (the one that just resolved off the
   * stack and triggered onAttackPlay). Updates the actor's adValueOverrides
   * for the just-pushed combat slot's copy instance, so CombatDock and
   * ActiveCombatPopup display the boosted value AND resolveCombat uses it
   * for damage. Only meaningful inside `onAttackPlay` results.
   */
  boostCurrentAttackPower?: number;
  /**
   * Subtract N power from the OPPOSING attack currently in the combat slot
   * (the unresolved slot owned by the opponent that this defend is blocking).
   * Updates the opponent's adValueOverrides for that attack's copy instance,
   * so CombatDock / ActiveCombatPopup display the weakened value AND
   * resolveCombat uses it for damage. Power is clamped to 0 minimum.
   * Only meaningful inside `onDefendPlay` results.
   */
  weakenOpposingAttackPower?: number;
  /** Grant a keyword to one of SELF's equip slots that will apply the next time
   * that equip is played as an attack this round. Honored from ANY hook
   * (unlike slotKeywordGrants which is play-time only). Cleared at round start. */
  grantSelfEquipSlotKeyword?: { slotIdx: number; keyword: string };
  /** Queue one or more buffs onto SELF's pendingBuffs list. Built via makeBuff().
   * Preferred over selfPatch.pendingBuffs because it routes through queueBuff()
   * which also emits a buff.queue trace event. */
  queueSelfBuff?: Buff | Buff[];
  /** Queue one or more buffs onto OPPONENT's pendingBuffs list. */
  queueOpponentBuff?: Buff | Buff[];
  /** Set the actor (self) as the new aggressor for this round. Flips aggressor
   *  to `actor`, hands priority to them, and resets subPhase to
   *  "aggressor-pre-attack". Used by cards like Pocket Sand which, after
   *  negating an attack and stunning the attacker, swap the combat roles so
   *  the defender takes over as aggressor (mirrors a stun attack landing). */
  becomeAggressor?: boolean;
  /** Negate the most-recent unresolved combat slot owned by the OPPONENT.
   *  Removes the slot, decrements attacksThisRound (game + attacker), and
   *  hands priority back to the attacker at `aggressor-pre-attack`. Unlike
   *  stack-level Negate, this targets a combat slot that has already resolved
   *  off the stack — used by Reversal (sig utility played after 2 counters
   *  while an attack sits in the combat slot). Does NOT refund stamina,
   *  weapon-tap, or sig-inPlay (the attacker keeps what they spent). Also
   *  resets `consecutiveCounters` so Reversal can't repeat-fire off stale
   *  counter state. Only meaningful from `onUtilityPlay`. */
  negateInPlaySlot?: boolean;
  /**
   * Return the just-played A/D card back to the actor's hand. Used by
   * Wraith Spin (onDamageDealt) and the equivalent defend hook. The engine:
   *   1. Finds the ZoneCard in `attackDefendZone` matching `instanceId`
   *   2. Removes it (and any matching handInstance copy already there)
   *   3. Pushes a fresh handInstance back into self.hand + handInstances
   *   4. Sets `self.onceFlagsThisRound[flagKey]` so per-round limits work
   * Hooks must include a `flagKey` (e.g. "wraithSpin-return") and the
   * engine will refuse to re-return if the flag is already set.
   * `mode` lets the engine show the right log line; both return to hand.
   */
  returnSelfToHand?: {
    instanceId: string;
    mode: "attack" | "defend";
    flagKey: string;
  };
  /**
   * Phase 3 — Doubled Block: when returned from `onDefendPlay`, opens a
   * free extra-defend window so the same player can attach one more defend
   * card to the active combat slot. The engine sets
   * `gs.doubleDefendPendingSlotId` to `slotId` and gives the defender
   * priority to play a second defend (or pass). Cleared when
   * `attachDoubleDefend` is called or the player passes.
   *
   * Only meaningful inside `onDefendPlay` results. Engine reads the
   * `slotId` from the CardHookCtx automatically — just set this to `true`.
   */
  openDoubleDefendWindow?: boolean;
  /**
   * Phase 3 — Vengeance Strike / onSelfTookDamage: when returned from the
   * new `onSelfTookDamage` hook, opens a prompt asking the player to pay
   * `staminaCost` stamina and play this card as the round's attack.
   * Engine deducts stamina and routes through the normal attack-play path.
   */
  vengeanceAttackPrompt?: {
    staminaCost: number;
    minDamage: number;
    instanceId: string;
  };
}

/**
 * Per-card playability gate. Returns `{ enabled: false, reason }` to block
 * the play action with a UI-visible reason; `{ enabled: true }` to allow.
 *
 * `canPlayAttack` / `canPlayDefend` / `canPlayUtility` are consulted by the
 * CardActionPopup's eval* functions AFTER the generic priority/window/stunned
 * checks pass. Use them for card-text restrictions like Bloodied Edge's
 * “requires 2 damaging attacks this round”.
 */
export interface CardPlayGate {
  enabled: boolean;
  reason?: string;
}

export interface CardHooks {
  onAttackPlay?: (ctx: CardHookCtx) => CardHookResult;
  onDefendPlay?: (ctx: CardHookCtx) => CardHookResult;
  onUtilityPlay?: (ctx: CardHookCtx) => CardHookResult;
  onDamageDealt?: (ctx: CardHookCtx) => CardHookResult;
  onDamageTaken?: (ctx: CardHookCtx) => CardHookResult;
  onFullBlock?: (ctx: CardHookCtx) => CardHookResult;
  onRoundStart?: (ctx: CardHookCtx) => CardHookResult;
  onRoundEnd?: (ctx: CardHookCtx) => CardHookResult;
  onEnter?: (ctx: CardHookCtx) => CardHookResult; // for armor / champion / equipment entering
  /**
   * Phase 3 — Vengeance Strike: called on cards in the player's hand after
   * they take damage. `ctx.damageTaken` carries the damage amount. Return
   * `vengeanceAttackPrompt` to surface a "pay X stamina to play this as
   * your attack" prompt. Only fires on A/D cards when an attack slot is
   * currently open (i.e. the player hasn't already committed an attack
   * this round).
   */
  onSelfTookDamage?: (ctx: CardHookCtx) => CardHookResult;
  /**
   * Phase 3 — Sharpening Mill: called when the player activates an in-play
   * Item. Only fired if the card is in `player.utilityInPlay` and the
   * round's once-flag for this instance has not been set yet.
   */
  onActivate?: (ctx: CardHookCtx) => CardHookResult;
  /** Optional card-specific playability check for the attack action. */
  canPlayAttack?: (ctx: CardHookCtx) => CardPlayGate;
  /** Optional card-specific playability check for the defend action. */
  canPlayDefend?: (ctx: CardHookCtx) => CardPlayGate;
  /** Optional card-specific playability check for the utility/Play action
   *  (utility cards AND utility-mode signature moves). */
  canPlayUtility?: (ctx: CardHookCtx) => CardPlayGate;
}

const REGISTRY: Record<string, CardHooks> = {
  "This is Sparta": thisIsSparta,
  "Assisting Saber": assistingSaber,
  Soulreaver: soulreaver,
  "Aegis the Defiant": aegisTheDefiant,
  "Spartacus, Undefeated": spartacusUndefeated,
  "Hardened Steel": hardenedSteel,
  "Handle Bash": handleBash,
  "The First Fight": theFirstFight,
  "Ignite Blade": igniteBlade,
  Clobberation: clobberation,
  "Dance of Blades": danceOfBlades,
  "Hardened Hands": hardenedHands,
  "Unbroken Sands": unbrokenSands,
  "Zeus' Wrathbrand": zeusWrathbrand,
  "Added Pressure": addedPressure,
  "Concussion Ring": concussionRing,
  "Thrusting Release": thrustingRelease,
  "Fleeting Cut": fleetingCut,
  "Saw It Coming": sawItComing,
  "Pocket Sand": pocketSand,
  "Hookfang Shiv": hookfangShiv,
  "Bloodforged Plate": bloodforgedPlate,
  "Bloodied Edge": bloodiedEdge,
  "Relentless End": relentlessEnd,
  "Reversal": reversal,
  "Blind-Side Follow Up": blindSideFollowUp,
  "Follow Up": followUp,
  "Shoulder Bash": shoulderBash,
  "Sharpening Mill": sharpeningMill,
  "Fluid Motion": fluidMotion,
  "Ready Arms": readyArms,
  "Pick Up Your Weapon": pickUpYourWeapon,
  "Iron Break Sigil": ironBreakSigil,
  "No Time": noTime,
  "Serpent's Prick": serpentsPrick,
  "The Final Straw": theFinalStraw,
  "Wraith Spin": wraithSpin,
  "Crimson Tempest": crimsonTempest,
  // Phase 3
  "Chain The Flow": chainTheFlow,
  "Vengeance Strike": vengeanceStrike,
  "Calculate Risk": calculateRisk,
  "Doubled Block": doubledBlock,
};

export function getCardHooks(card: Card): CardHooks | undefined {
  return REGISTRY[canonicalName(card.name)];
}

/**
 * Hook keys whose handlers return `CardHookResult`. Excludes `canPlayAttack`,
 * which has its own `CardPlayGate` return type — use `getCardHooks(card)
 * ?.canPlayAttack?.(ctx)` directly for that one.
 */
type CardEffectHookKey = Exclude<
  keyof CardHooks,
  "canPlayAttack" | "canPlayDefend" | "canPlayUtility"
>;

export function callHook<K extends CardEffectHookKey>(
  card: Card,
  hook: K,
  ctx: CardHookCtx,
): CardHookResult {
  const hooks = getCardHooks(card);
  const fn = hooks?.[hook];
  if (!fn) return {};
  return fn(ctx) ?? {};
}

export interface ChampionHooks {
  onAttackPlay?: (ctx: CardHookCtx) => CardHookResult;
  onDefendPlay?: (ctx: CardHookCtx) => CardHookResult;
  onUtilityPlay?: (ctx: CardHookCtx) => CardHookResult;
  onDamageDealt?: (ctx: CardHookCtx) => CardHookResult;
  onDamageTaken?: (ctx: CardHookCtx) => CardHookResult;
  onFullBlock?: (ctx: CardHookCtx) => CardHookResult;
  onRoundStart?: (ctx: CardHookCtx) => CardHookResult;
  onRoundEnd?: (ctx: CardHookCtx) => CardHookResult;
  onEnter?: (ctx: CardHookCtx) => CardHookResult;
}

const CHAMPION_REGISTRY: Record<string, ChampionHooks> = {
  "Crixus, Gaul-Breaker": crixusGaulBreaker,
};

/**
 * Look up champion hooks by the champion card's NAME (not its instance id).
 * Champion instance ids look like "crd_abc123"; the CHAMPION_REGISTRY is
 * keyed by canonical card name. Resolve the Card via cardMap first, then
 * pass card.name here.
 */
export function getChampionHooks(name: string | null | undefined): ChampionHooks | undefined {
  return CHAMPION_REGISTRY[canonicalName(name ?? "")];
}

/**
 * Convenience: resolve a player's equipped champion card and its hooks in one
 * shot. Returns { card: undefined, hooks: undefined } when no champion is
 * equipped or the cardMap is missing the instance.
 */
export function getChampionHooksFor(
  gs: GameState,
  n: 1 | 2,
  cardMap: Map<string, Card>,
): { card: Card | undefined; hooks: ChampionHooks | undefined } {
  const id = gs.players[n].championCardId;
  const card = id ? cardMap.get(id) : undefined;
  const hooks = card ? getChampionHooks(card.name) : undefined;
  return { card, hooks };
}

/**
 * Unified hook lookup for a card. Tries the regular REGISTRY first, then
 * CHAMPION_REGISTRY. Used by passive damage loops in playtestEngine that
 * iterate over slot card ids (champion / armor / equipment) and need to
 * fire hooks regardless of which registry the card lives in.
 *
 * Champion-only cards (e.g. Crixus, Gaul-Breaker) intentionally live only
 * in CHAMPION_REGISTRY so we don't dispatch their hooks when the same card
 * appears in someone's hand or deck. This helper bridges that for the
 * narrow case of passive in-play triggers.
 */
export function getAnyHooksByName(name: string | null | undefined): CardHooks | undefined {
  const key = canonicalName(name ?? "");
  return REGISTRY[key] ?? CHAMPION_REGISTRY[key];
}
